/**
 * TableSearch — reusable debounced search input for filtering table rows.
 *
 * Usage:
 *   <TableSearch placeholder="Search by ticker..." onSearch={setQuery} />
 *
 * onSearch is called with the trimmed filter string after a 300 ms debounce.
 * Passing an empty string means "clear the filter".
 */
import { useEffect, useRef, useState } from 'react'
import { Search, X } from 'lucide-react'
import { clsx } from 'clsx'

interface TableSearchProps {
  placeholder?: string
  onSearch: (query: string) => void
  className?: string
  /** Initial value (uncontrolled default) */
  defaultValue?: string
}

export default function TableSearch({
  placeholder = 'Search...',
  onSearch,
  className,
  defaultValue = '',
}: TableSearchProps) {
  const [value, setValue] = useState(defaultValue)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Debounce: call onSearch 300 ms after the user stops typing
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      onSearch(value.trim())
    }, 300)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [value, onSearch])

  const clear = () => {
    setValue('')
    onSearch('')
  }

  return (
    <div className={clsx('relative', className)}>
      <Search
        size={14}
        className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
        aria-hidden
      />
      <input
        type="text"
        value={value}
        onChange={e => setValue(e.target.value)}
        placeholder={placeholder}
        className="pl-8 pr-8 py-2 bg-surface-card border border-border rounded-lg text-sm text-text-primary w-48 focus:outline-none focus:ring-1 focus:ring-blue"
      />
      {value && (
        <button
          type="button"
          onClick={clear}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
          aria-label="Clear search"
        >
          <X size={13} />
        </button>
      )}
    </div>
  )
}
